import { Grid, GridProps, Icon, Stack, Text, Tooltip } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { ReactNode, useMemo } from 'react';
import { HiInformationCircle } from 'react-icons/hi2';

type Props = GridProps & {
  label: string | ReactNode;
  value?: string | number | ReactNode;
  tooltip?: ReactNode;
  children?: ReactNode;
  defaultValue?: string | number | ReactNode;
  minColumnWidth?: string | number;
  hideIfEmpty?: boolean;
};

type RightAlignedLabeledValueProps = {
  label: ReactNode;
  value?: ReactNode;
  minWidth?: string | number;
};

export const LabeledValue = ({
  label,
  value,
  tooltip,
  children,
  defaultValue,
  hideIfEmpty = false,
  minColumnWidth = '120px',
  ...rest
}: Props) => {
  const displayLabel = useMemo(() => {
    if (tooltip) {
      return (
        <Tooltip label={tooltip} fontSize="sm" bg="gray.800" color="white" p="2" w="full">
          <Stack direction="row" align="center" spacing="1">
            <Text color="text.subtle">{label}</Text>
            <Icon as={HiInformationCircle} fontSize="12px" color="text.subtle" />
          </Stack>
        </Tooltip>
      );
    }

    return <Text color="text.subtle">{label}</Text>;
  }, [label, tooltip]);

  if (hideIfEmpty && !value) return null;

  return (
    <Grid
      gridTemplateColumns={`minmax(${minColumnWidth}, max-content) 1fr`}
      columnGap={3}
      alignItems="start"
      fontSize="sm"
      {...rest}
    >
      <Stack direction="row" spacing={0}>
        {displayLabel}
      </Stack>
      <Stack direction="row" spacing={1} align="start" wrap="wrap">
        <Show if={!!value}>
          <Text
            textStyle="bodySmallSemibold"
            fontSize="inherit"
            lineHeight="inherit"
            whiteSpace="preserve"
            align="start"
          >
            {value}
          </Text>
        </Show>
        <Show if={!!children}>{children}</Show>
        <Show if={!value && !children && !!defaultValue}>
          <Text textStyle="bodySmallSemibold" lineHeight="inherit" whiteSpace="preserve" align="start">
            {defaultValue}
          </Text>
        </Show>
      </Stack>
    </Grid>
  );
};

/**
 * Quote list cards display value-first and right-aligned in a compact stacked layout.
 */
export const RightAlignedLabeledValue = ({
  label,
  value,
  minWidth = '90px',
}: RightAlignedLabeledValueProps) => {
  return (
    <Stack direction="column" align="right" spacing={1} minW={minWidth}>
      <Text textAlign="right">{value}</Text>
      <Text textStyle="captionSmallMedium" color="text.subtle" textAlign="right">
        {label}
      </Text>
    </Stack>
  );
};
